/** Small vendored primitives (shadcn/ui style, editable in place).
 *
 * Grouped in one module while the set is small; split per-component as it grows.
 */

import type { ComponentProps, ReactNode } from 'react'

import { cn } from '@/lib/cn'

export function Card({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'rounded-[var(--radius-card)] bg-[var(--surface-1)] ring-1 ring-[var(--border)]',
        className,
      )}
      {...props}
    />
  )
}

export function CardHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex items-center justify-between gap-3 px-4 py-3', className)}
      {...props}
    />
  )
}

export function CardTitle({ className, ...props }: ComponentProps<'h3'>) {
  return <h3 className={cn('text-[15px] font-semibold', className)} {...props} />
}

export function CardBody({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('px-4 py-3', className)} {...props} />
}

export function Separator({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      role="separator"
      className={cn('h-px w-full bg-[var(--border)]', className)}
      {...props}
    />
  )
}

export function Input({ className, ...props }: ComponentProps<'input'>) {
  return (
    <input
      className={cn(
        'h-9 w-full rounded-[var(--radius-control)] bg-[var(--surface-raised)] px-3 text-sm',
        'ring-1 ring-[var(--border)] placeholder:text-[var(--text-muted)]',
        'focus:ring-2 focus:ring-[var(--focus-ring)] focus:outline-none',
        className,
      )}
      {...props}
    />
  )
}

export function Select({ className, children, ...props }: ComponentProps<'select'>) {
  return (
    <select
      className={cn(
        'h-9 w-full rounded-[var(--radius-control)] bg-[var(--surface-raised)] px-2 text-sm',
        'ring-1 ring-[var(--border)] focus:ring-2 focus:ring-[var(--focus-ring)] focus:outline-none',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  )
}

export function Checkbox({ className, ...props }: ComponentProps<'input'>) {
  return (
    <input
      type="checkbox"
      className={cn(
        'size-4 shrink-0 rounded-[3px] accent-[var(--series-1)] ring-1 ring-[var(--border)]',
        className,
      )}
      {...props}
    />
  )
}

export function Label({ className, ...props }: ComponentProps<'label'>) {
  return (
    <label
      className={cn('text-xs font-medium text-[var(--text-secondary)]', className)}
      {...props}
    />
  )
}

export function Chip({
  className,
  tone = 'neutral',
  children,
  title,
}: {
  className?: string
  tone?: 'neutral' | 'accent' | 'muted'
  children: ReactNode
  title?: string
}) {
  const tones = {
    neutral: 'bg-[var(--surface-sunken)] text-[var(--text-secondary)]',
    accent: 'bg-[color-mix(in_oklab,var(--series-1)_14%,transparent)] text-[var(--series-1)]',
    muted: 'bg-transparent text-[var(--text-muted)] ring-1 ring-[var(--border)]',
  } as const
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

export function Skeleton({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('animate-pulse rounded bg-[var(--surface-sunken)]', className)}
      {...props}
    />
  )
}

export function Table({ className, ...props }: ComponentProps<'table'>) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn('w-full border-collapse text-[13px]', className)} {...props} />
    </div>
  )
}

export function Th({ className, ...props }: ComponentProps<'th'>) {
  return (
    <th
      className={cn(
        'sticky top-0 z-10 border-b border-[var(--border)] bg-[var(--surface-sunken)]',
        'px-3 py-2 text-left text-[11px] font-semibold tracking-wide text-[var(--text-secondary)]',
        className,
      )}
      {...props}
    />
  )
}

export function Td({ className, ...props }: ComponentProps<'td'>) {
  return (
    <td
      className={cn('border-b border-[var(--border)] px-3 py-2 align-middle', className)}
      {...props}
    />
  )
}

export function Tr({ className, ...props }: ComponentProps<'tr'>) {
  return <tr className={cn('hover:bg-[var(--surface-sunken)]', className)} {...props} />
}
